import React, { useEffect, useState } from 'react';

import { Paper, Stack } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistRuleCard from '../../reportCards/ChecklistRuleCard';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import type { ManualOverrideState } from '../../types';

import type { DatesRestrictionsChecklistRow } from './types';

interface Props {
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<DatesRestrictionsChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DatesRestrictionsReportCard({
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedRows(rows.reduce<Record<string, boolean>>((nextRows, row) => ({ ...nextRows, [row.id]: true }), {}));
  }, [rows]);

  return (
    <Stack spacing={1.5}>
      {rows.length === 0 ? (
        <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
          No Dates &amp; Restrictions rows match this view.
        </Paper>
      ) : (
        rows.map((row) => {
          const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
          const approved = isRowApproved(row, manualOverrides);
          const isExpanded = expandedRows[row.id] ?? true;

          return (
            <ChecklistRuleCard
              key={row.id}
              approved={approved}
              dataPoints={row.details.map((detail) => ({
                label: detail.label,
                readOnly: true,
                value: detail.value,
              }))}
              expanded={isExpanded}
              manuallyApproved={manuallyApproved}
              rule={row.rule}
              summary={row.summary}
              title={row.title}
              onApprovalChange={() => onApprovalChange(row.id)}
              onUnapprovalChange={() => onUnapprovalChange(row.id)}
              onToggleExpanded={() => setExpandedRows((currentRows) => ({ ...currentRows, [row.id]: !isExpanded }))}
            />
          );
        })
      )}
      <HiddenApprovedFooter
        count={hiddenApprovedCount}
        noun="Dates & Restrictions row"
        showAutoApproved={showAutoApproved}
      />
    </Stack>
  );
}
